// ─────────────────────────────────────────────────────────────────────────
// Admin → Creative Strategist (Task #2376).
//
// Two panes:
//   General          — the global switch plus every knob the AI Decision
//                      Gateway reads at call time.
//   Templates        — every brand's template coverage, with approve / default
//                      / retire, the underlying PersonaOS reference and how
//                      often each approval has actually been selected.
//   AI Decision Points — one row per registered decision, and a detail view
//                      that documents the contract (purpose, trigger, schemas,
//                      examples, version history) and exposes the operational
//                      controls (provider, policy, endpoint, auth, timeout,
//                      retry), the endpoint tester and the activation gate.
//
// Every decision listed here comes from the code registry, so this screen is
// always a complete inventory — a key that exists in code but has not been
// seeded still appears, marked "not seeded".
// ─────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  Check,
  CircleSlash,
  Compass,
  ExternalLink,
  Layers,
  Loader2,
  PlayCircle,
  RefreshCw,
  Settings2,
  X,
} from "lucide-react";

// ── types (mirror of the admin serializers in creative-strategist-routes) ───

type GlobalMode = "enabled" | "disable_new_work" | "hard_stop";

interface GeneralSettings {
  globalMode: GlobalMode;
  defaultBrandFeatureState: boolean;
  maxConcurrentJobs: number;
  maxAutomaticRevisions: number;
  defaultInternalModel: string;
  defaultSimTimeoutMs: number;
  allowSimFallback: boolean;
  pauseAllJobs: boolean;
  updatedAt: string | null;
}

interface DecisionListRow {
  key: string;
  name: string;
  description: string;
  category: string;
  version: number;
  provider: "internal" | "sim";
  executionPolicy: "internal_only" | "sim_only" | "sim_with_fallback";
  timeoutMs: number;
  endpointUrl: string | null;
  active: boolean;
  seeded: boolean;
  lastTestStatus: string | null;
  lastTestAt: string | null;
  updatedAt: string | null;
}

interface DecisionRun {
  id: string;
  projectId: string | null;
  taskId: string | null;
  brandId: string | null;
  decisionKey: string;
  decisionVersion: number;
  provider: string;
  modelOrEndpoint: string | null;
  validationStatus: string;
  durationMs: number;
  retryCount: number;
  error: string | null;
  createdAt: string | null;
}

interface DecisionDetail {
  key: string;
  name: string;
  description: string;
  category: string;
  whenItRuns: string;
  version: number;
  versionHistory: { version: number; date: string; notes: string }[];
  instruction: string;
  defaults: {
    provider: "internal" | "sim";
    executionPolicy: string;
    inputEnvelope: string;
    timeoutMs: number;
    retryPolicy: { maxRetries: number; baseDelayMs: number; multiplier: number };
  };
  provider: "internal" | "sim";
  executionPolicy: "internal_only" | "sim_only" | "sim_with_fallback";
  inputEnvelope: "wrapped" | "top_level";
  endpointUrl: string | null;
  authSecretKey: string | null;
  authHeaderName: string | null;
  timeoutMs: number;
  retryPolicy: { maxRetries: number; baseDelayMs: number; multiplier: number };
  active: boolean;
  seeded: boolean;
  inputSchema: unknown;
  outputSchema: unknown;
  exampleInput: Record<string, unknown>;
  exampleOutput: Record<string, unknown>;
  lastTest: {
    status: string | null;
    endpointUrl: string | null;
    at: string | null;
    detail: unknown;
  } | null;
  activation: { canActivate: boolean; reason: string | null };
  recentRuns: DecisionRun[];
  recentErrors: DecisionRun[];
}

interface EndpointTestResult {
  decisionKey: string;
  endpointUrl: string;
  requestBody: Record<string, unknown>;
  httpStatus: number | null;
  rawOutput: string;
  parsedOutput: Record<string, unknown> | null;
  validation: {
    ran: boolean;
    valid: boolean;
    errors: { path: string; message: string; kind: string }[];
    missingFields: string[];
    wrongTypes: string[];
  };
  runtimeMs: number;
  error: string | null;
  status: "passed" | "failed";
}

// ── shared bits ─────────────────────────────────────────────────────────────

const GLOBAL_MODES: { value: GlobalMode; label: string; description: string }[] = [
  { value: "enabled", label: "Enabled", description: "Normal operation for every brand that has the feature turned on." },
  { value: "disable_new_work", label: "Disable New Work", description: "Refuse new projects, tasks and job dispatch. Work already in flight finishes." },
  { value: "hard_stop", label: "Hard Stop", description: "Feature closed: every Creative Strategist page and API is turned off." },
];

const POLICY_LABELS: Record<string, string> = {
  internal_only: "Internal Only",
  sim_only: "Sim Only",
  sim_with_fallback: "Sim With Fallback",
};

const INTERNAL_MODELS = [
  "gpt-4o-mini",
  "gpt-4o",
  "gpt-4.1-mini",
  "gpt-4.1",
  "gpt-5-mini",
  "gpt-5",
];

const SETTINGS_KEY = ["/api/admin/creative-strategist-settings"];
const DECISIONS_KEY = ["/api/admin/creative-strategist/decisions"];
const RUNS_KEY = ["/api/admin/creative-strategist/runs"];

function JsonBlock({ value, testId }: { value: unknown; testId?: string }) {
  return (
    <pre
      className="max-h-72 overflow-auto rounded-md bg-muted/60 p-3 text-[11px] leading-relaxed"
      data-testid={testId}
    >
      {typeof value === "string" ? value : JSON.stringify(value, null, 2)}
    </pre>
  );
}

function ProviderBadge({ provider }: { provider: string }) {
  return (
    <Badge variant={provider === "sim" ? "default" : "secondary"} className="font-mono text-[10px]">
      {provider === "sim" ? "SIM" : "INTERNAL"}
    </Badge>
  );
}

function StatusDot({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      <span className={`h-2 w-2 rounded-full ${ok ? "bg-emerald-500" : "bg-muted-foreground/40"}`} />
      {label}
    </span>
  );
}

function fmtTime(v: string | null | undefined): string {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

// ── General ─────────────────────────────────────────────────────────────────

function GeneralPane() {
  const { toast } = useToast();
  const { data: settings, isLoading } = useQuery<GeneralSettings>({ queryKey: SETTINGS_KEY });
  const [draft, setDraft] = useState<GeneralSettings | null>(null);

  useEffect(() => {
    if (settings) setDraft(settings);
  }, [settings]);

  const save = useMutation({
    mutationFn: async (patch: Partial<GeneralSettings>) => {
      const res = await apiRequest("PUT", "/api/admin/creative-strategist-settings", patch);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
      toast({ title: "Saved", description: "Creative Strategist settings updated." });
    },
    onError: (e: Error) =>
      toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  const current = draft ?? settings ?? null;
  const dirty =
    !!current &&
    !!settings &&
    (["defaultBrandFeatureState", "maxConcurrentJobs", "maxAutomaticRevisions", "defaultInternalModel", "defaultSimTimeoutMs", "allowSimFallback", "pauseAllJobs"] as const).some(
      (k) => current[k] !== settings[k],
    );

  const set = <K extends keyof GeneralSettings>(k: K, v: GeneralSettings[K]) =>
    setDraft((d) => (d ? { ...d, [k]: v } : d));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Compass className="h-5 w-5" />
            Global mode
          </CardTitle>
          <CardDescription>
            Applies to every brand at once. A brand still needs its own Creative Strategist feature
            flag — this switch can only take access away, never grant it.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {GLOBAL_MODES.map((mode) => {
            const active = (settings?.globalMode ?? "enabled") === mode.value;
            return (
              <button
                key={mode.value}
                type="button"
                disabled={isLoading || save.isPending}
                onClick={() => !active && save.mutate({ globalMode: mode.value })}
                className={`w-full flex items-start gap-3 rounded-lg border p-4 text-left transition-colors ${
                  active ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                }`}
                data-testid={`button-creative-strategist-mode-${mode.value}`}
              >
                <div
                  className={`mt-0.5 h-4 w-4 rounded-full border-2 shrink-0 ${
                    active ? "border-primary bg-primary" : "border-muted-foreground/40"
                  }`}
                />
                <div className="space-y-0.5">
                  <div className="text-sm font-medium">{mode.label}</div>
                  <div className="text-xs text-muted-foreground">{mode.description}</div>
                </div>
              </button>
            );
          })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings2 className="h-5 w-5" />
            Execution defaults
          </CardTitle>
          <CardDescription>
            Read by the AI Decision Gateway on every call — no redeploy needed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {!current ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-6 rounded-lg border p-4">
                <div className="space-y-0.5">
                  <div className="text-sm font-medium">Default brand feature state</div>
                  <div className="text-xs text-muted-foreground">
                    Whether a brand created from now on starts with Creative Strategist on.
                  </div>
                </div>
                <Switch
                  checked={current.defaultBrandFeatureState}
                  onCheckedChange={(v) => set("defaultBrandFeatureState", v)}
                  data-testid="switch-cs-default-brand-state"
                />
              </div>

              <div className="flex items-start justify-between gap-6 rounded-lg border p-4">
                <div className="space-y-0.5">
                  <div className="text-sm font-medium">Allow Sim fallback</div>
                  <div className="text-xs text-muted-foreground">
                    Master switch for the Sim With Fallback policy. Off, those decisions behave like
                    Sim Only — they never quietly fall back to internal AI.
                  </div>
                </div>
                <Switch
                  checked={current.allowSimFallback}
                  onCheckedChange={(v) => set("allowSimFallback", v)}
                  data-testid="switch-cs-allow-sim-fallback"
                />
              </div>

              <div className="flex items-start justify-between gap-6 rounded-lg border border-amber-500/40 bg-amber-500/5 p-4">
                <div className="space-y-0.5">
                  <div className="text-sm font-medium">Pause all jobs</div>
                  <div className="text-xs text-muted-foreground">
                    Every decision returns a typed "paused" failure until this is turned back off.
                  </div>
                </div>
                <Switch
                  checked={current.pauseAllJobs}
                  onCheckedChange={(v) => set("pauseAllJobs", v)}
                  data-testid="switch-cs-pause-all-jobs"
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="cs-max-concurrent">Max concurrent jobs</Label>
                  <Input
                    id="cs-max-concurrent"
                    type="number"
                    min={1}
                    max={50}
                    value={current.maxConcurrentJobs}
                    onChange={(e) => set("maxConcurrentJobs", Number(e.target.value))}
                    data-testid="input-cs-max-concurrent"
                  />
                  <p className="text-xs text-muted-foreground">
                    Decisions running at once across the whole app. Extra calls queue.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="cs-max-revisions">Max automatic revisions</Label>
                  <Input
                    id="cs-max-revisions"
                    type="number"
                    min={0}
                    max={10}
                    value={current.maxAutomaticRevisions}
                    onChange={(e) => set("maxAutomaticRevisions", Number(e.target.value))}
                    data-testid="input-cs-max-revisions"
                  />
                  <p className="text-xs text-muted-foreground">
                    How many times a deliverable may be auto-revised before a human is asked.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="cs-default-model">Default internal model</Label>
                  <Select
                    value={current.defaultInternalModel}
                    onValueChange={(v) => set("defaultInternalModel", v)}
                  >
                    <SelectTrigger id="cs-default-model" data-testid="select-cs-default-model">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from(new Set([...INTERNAL_MODELS, current.defaultInternalModel])).map(
                        (m) => (
                          <SelectItem key={m} value={m}>
                            {m}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Used by every decision on the internal provider.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="cs-sim-timeout">Default Sim timeout (ms)</Label>
                  <Input
                    id="cs-sim-timeout"
                    type="number"
                    min={1000}
                    max={900000}
                    step={1000}
                    value={current.defaultSimTimeoutMs}
                    onChange={(e) => set("defaultSimTimeoutMs", Number(e.target.value))}
                    data-testid="input-cs-sim-timeout"
                  />
                  <p className="text-xs text-muted-foreground">
                    Applied to Sim-backed decisions that do not set their own timeout.
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between border-t pt-4">
                <span className="text-xs text-muted-foreground">
                  Last updated {fmtTime(settings?.updatedAt)}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!dirty || save.isPending}
                    onClick={() => settings && setDraft(settings)}
                    data-testid="button-cs-general-reset"
                  >
                    Reset
                  </Button>
                  <Button
                    size="sm"
                    disabled={!dirty || save.isPending}
                    onClick={() => {
                      const { globalMode, updatedAt, ...rest } = current;
                      save.mutate(rest);
                    }}
                    data-testid="button-cs-general-save"
                  >
                    {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save changes
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Runs (spec §62) ─────────────────────────────────────────────────────────
//
// One screen for everything Creative Strategist currently has in flight:
// active projects, live tasks, the executor's attempts with the underlying
// generator run behind each one, and the decision runs. Retry and cancel go
// through the same executor entry points the product UI uses.

interface AdminRunProject {
  id: string;
  title: string;
  status: string;
  planStatus: string;
  brandId: string;
  brandName: string | null;
  userName: string | null;
  updatedAt: string | null;
}

interface AdminRunTask {
  id: string;
  projectId: string;
  projectTitle: string | null;
  brandName: string | null;
  title: string;
  status: string;
  assignedExecutor: string | null;
  attemptCount: number;
  lastError: string | null;
  updatedAt: string | null;
}

interface AdminRunExecution {
  id: string;
  taskId: string;
  executionKey: string;
  attemptNumber: number;
  executor: string;
  status: string;
  underlyingSystem: string | null;
  underlyingRunId: string | null;
  underlyingHref: string | null;
  lastError: string | null;
  durationMs: number | null;
  taskTitle: string | null;
  taskStatus: string | null;
  projectTitle: string | null;
  brandName: string | null;
  userName: string | null;
  createdAt: string | null;
  completedAt: string | null;
}

interface AdminRunDecisionRun {
  id: string;
  decisionKey: string;
  status: string;
  validationStatus: string;
  error: string | null;
  durationMs: number | null;
  createdAt: string | null;
}

interface AdminRunsPayload {
  globalMode: GlobalMode;
  maxConcurrentJobs: number;
  pauseAllJobs: boolean;
  projects: AdminRunProject[];
  activeTasks: AdminRunTask[];
  executions: AdminRunExecution[];
  decisionRuns: AdminRunDecisionRun[];
}

const RUN_ACTIVE_STATUSES = new Set(["pending", "dispatched", "running"]);

function formatDuration(ms: number | null): string {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

function formatWhen(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function RunsPane() {
  const { toast } = useToast();
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [inspectId, setInspectId] = useState<string | null>(null);

  const queryKey = useMemo(
    () => [`${RUNS_KEY[0]}${errorsOnly ? "?errorsOnly=1" : ""}`],
    [errorsOnly],
  );
  const { data, isLoading, isFetching, refetch } = useQuery<AdminRunsPayload>({
    queryKey,
    // Poll only while something is actually running.
    refetchInterval: (query) => {
      const d = query.state.data as AdminRunsPayload | undefined;
      const live =
        (d?.executions ?? []).some((e) => RUN_ACTIVE_STATUSES.has(e.status)) ||
        (d?.activeTasks ?? []).some((t) => t.status === "running");
      return live ? 5000 : false;
    },
  });

  const act = useMutation({
    mutationFn: async (args: { taskId: string; action: "retry" | "cancel" }) => {
      const res = await apiRequest(
        "POST",
        `/api/admin/creative-strategist/runs/${args.taskId}/${args.action}`,
      );
      return (await res.json()) as { message?: string };
    },
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: "Done", description: r?.message ?? "Applied." });
    },
    onError: (e: any) =>
      toast({
        title: "That did not work",
        description: e?.message ?? "Please try again.",
        variant: "destructive",
      }),
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading runs…
      </div>
    );
  }

  const payload = data;
  return (
    <div className="space-y-6" data-testid="cs-runs-pane">
      <div className="flex flex-wrap items-center gap-3">
        <Badge variant="outline" className="text-[10px]">
          Switch: {payload?.globalMode ?? "—"}
        </Badge>
        <Badge variant="outline" className="text-[10px]">
          Max concurrent: {payload?.maxConcurrentJobs ?? "—"}
        </Badge>
        {payload?.pauseAllJobs ? (
          <Badge variant="destructive" className="text-[10px]">
            All jobs paused
          </Badge>
        ) : null}
        <div className="ml-auto flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <Switch
              checked={errorsOnly}
              onCheckedChange={setErrorsOnly}
              data-testid="switch-runs-errors-only"
            />
            Errors only
          </label>
          <Button
            size="sm"
            variant="outline"
            onClick={() => refetch()}
            data-testid="button-runs-refresh"
          >
            {isFetching ? (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-3.5 w-3.5" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Active projects</CardTitle>
          <CardDescription>
            Every project that has not finished, with the brand and the person who started it.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {(payload?.projects ?? []).length === 0 ? (
            <div className="px-6 pb-6 text-sm text-muted-foreground">No active projects.</div>
          ) : (
            <div className="divide-y">
              {(payload?.projects ?? []).map((p) => (
                <div
                  key={p.id}
                  className="flex items-center gap-3 px-6 py-2.5 text-sm"
                  data-testid={`row-run-project-${p.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{p.title}</div>
                    <div className="truncate text-xs text-muted-foreground">
                      {p.brandName ?? p.brandId} · {p.userName ?? "unknown user"} ·{" "}
                      {formatWhen(p.updatedAt)}
                    </div>
                  </div>
                  <Badge variant="outline" className="text-[10px]">
                    {p.status}
                  </Badge>
                  <Badge variant="outline" className="text-[10px]">
                    plan {p.planStatus}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Live tasks</CardTitle>
          <CardDescription>Tasks that are ready, running or blocked right now.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {(payload?.activeTasks ?? []).length === 0 ? (
            <div className="px-6 pb-6 text-sm text-muted-foreground">Nothing in flight.</div>
          ) : (
            <div className="divide-y">
              {(payload?.activeTasks ?? []).map((t) => (
                <div
                  key={t.id}
                  className="flex items-center gap-3 px-6 py-2.5 text-sm"
                  data-testid={`row-run-task-${t.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{t.title}</div>
                    <div className="truncate text-xs text-muted-foreground">
                      {t.projectTitle ?? t.projectId} · {t.brandName ?? ""} ·{" "}
                      {t.assignedExecutor ?? "unassigned"} · attempt {t.attemptCount}
                    </div>
                    {t.lastError ? (
                      <div className="truncate text-xs text-red-600 dark:text-red-400">
                        {t.lastError}
                      </div>
                    ) : null}
                  </div>
                  <Badge variant="outline" className="text-[10px]">
                    {t.status}
                  </Badge>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={act.isPending}
                    onClick={() => act.mutate({ taskId: t.id, action: "retry" })}
                    data-testid={`button-run-retry-${t.id}`}
                  >
                    <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Retry
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={act.isPending || t.status !== "running"}
                    onClick={() => act.mutate({ taskId: t.id, action: "cancel" })}
                    data-testid={`button-run-cancel-${t.id}`}
                  >
                    <CircleSlash className="mr-1.5 h-3.5 w-3.5" /> Cancel
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Executions</CardTitle>
          <CardDescription>
            Every dispatch through a tool adapter, and the generator run behind it.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {(payload?.executions ?? []).length === 0 ? (
            <div className="px-6 pb-6 text-sm text-muted-foreground">No executions yet.</div>
          ) : (
            <div className="divide-y">
              {(payload?.executions ?? []).map((e) => (
                <div
                  key={e.id}
                  className="flex items-center gap-3 px-6 py-2.5 text-sm"
                  data-testid={`row-run-execution-${e.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">
                      {e.taskTitle ?? e.taskId}{" "}
                      <span className="text-xs text-muted-foreground">
                        attempt {e.attemptNumber}
                      </span>
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {e.executor} · {e.projectTitle ?? ""} · {e.brandName ?? ""} ·{" "}
                      {e.userName ?? "unknown user"} · {formatDuration(e.durationMs)} ·{" "}
                      {formatWhen(e.createdAt)}
                    </div>
                    {e.lastError ? (
                      <div className="truncate text-xs text-red-600 dark:text-red-400">
                        {e.lastError}
                      </div>
                    ) : null}
                  </div>
                  <Badge
                    variant={e.status === "failed" ? "destructive" : "outline"}
                    className="text-[10px]"
                  >
                    {e.status}
                  </Badge>
                  {e.underlyingHref ? (
                    <a
                      href={e.underlyingHref}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-muted-foreground underline-offset-2 hover:underline"
                      data-testid={`link-run-underlying-${e.id}`}
                    >
                      <ExternalLink className="mr-1 inline h-3 w-3" />
                      {e.underlyingSystem}
                    </a>
                  ) : null}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setInspectId(e.id)}
                    data-testid={`button-run-inspect-${e.id}`}
                  >
                    Inspect
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Recent decision runs</CardTitle>
          <CardDescription>The AI Decision Gateway calls behind this work.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {(payload?.decisionRuns ?? []).length === 0 ? (
            <div className="px-6 pb-6 text-sm text-muted-foreground">No decision runs yet.</div>
          ) : (
            <div className="divide-y">
              {(payload?.decisionRuns ?? []).map((r) => (
                <div
                  key={r.id}
                  className="flex items-center gap-3 px-6 py-2 text-sm"
                  data-testid={`row-run-decision-${r.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-mono text-xs">{r.decisionKey}</div>
                    {r.error ? (
                      <div className="truncate text-xs text-red-600 dark:text-red-400">
                        {r.error}
                      </div>
                    ) : null}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {formatDuration(r.durationMs)}
                  </span>
                  <Badge
                    variant={r.status === "success" ? "outline" : "destructive"}
                    className="text-[10px]"
                  >
                    {r.status}
                  </Badge>
                  <span className="text-xs text-muted-foreground">{formatWhen(r.createdAt)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {inspectId ? (
        <RunInspectDialog executionId={inspectId} onClose={() => setInspectId(null)} />
      ) : null}
    </div>
  );
}

function RunInspectDialog({
  executionId,
  onClose,
}: {
  executionId: string;
  onClose: () => void;
}) {
  const { data, isLoading } = useQuery<any>({
    queryKey: [`${RUNS_KEY[0]}/${executionId}`],
  });

  return (
    <Dialog open onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogContent className="max-w-3xl" data-testid="dialog-run-inspect">
        <DialogHeader>
          <DialogTitle className="text-sm">Execution detail</DialogTitle>
          <DialogDescription>
            What this attempt sent, what it produced, and everything recorded against the task.
          </DialogDescription>
        </DialogHeader>
        {isLoading || !data ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="max-h-[65vh] space-y-4 overflow-y-auto text-xs">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-muted-foreground">Execution key</div>
                <div className="font-mono break-all">{data.execution?.executionKey}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Status</div>
                <div>
                  {data.execution?.status} · {formatDuration(data.execution?.durationMs ?? null)}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Task</div>
                <div>
                  {data.task?.title ?? "—"} ({data.task?.status ?? "—"}, attempt{" "}
                  {data.task?.attemptCount ?? "—"})
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Underlying run</div>
                <div className="font-mono break-all">
                  {data.execution?.underlyingSystem ?? "—"}{" "}
                  {data.execution?.underlyingRunId ?? ""}
                </div>
              </div>
            </div>

            {data.execution?.lastError ? (
              <div className="rounded border border-red-200 bg-red-50 p-2 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
                {data.execution.lastError}
              </div>
            ) : null}

            <div>
              <div className="mb-1 font-medium">Inputs sent</div>
              <pre className="max-h-56 overflow-auto rounded bg-muted p-2 font-mono text-[10px]">
                {JSON.stringify(data.inputs ?? {}, null, 2)}
              </pre>
            </div>

            <div>
              <div className="mb-1 font-medium">Outputs</div>
              {(data.outputLinks ?? []).length === 0 ? (
                <div className="text-muted-foreground">Nothing recorded.</div>
              ) : (
                <div className="space-y-1">
                  {(data.outputLinks ?? []).map((l: any) => (
                    <div key={l.id} className="font-mono">
                      {l.outputSystem}/{l.outputType} · {l.outputId}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="mb-1 font-medium">Activity</div>
              {(data.activity ?? []).length === 0 ? (
                <div className="text-muted-foreground">Nothing recorded.</div>
              ) : (
                <div className="space-y-1">
                  {(data.activity ?? []).map((a: any) => (
                    <div key={a.id}>
                      {a.summary}{" "}
                      <span className="text-muted-foreground">{formatWhen(a.createdAt)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Decision list ───────────────────────────────────────────────────────────

function DecisionList({ onOpen }: { onOpen: (key: string) => void }) {
  const { data: rows = [], isLoading } = useQuery<DecisionListRow[]>({ queryKey: DECISIONS_KEY });

  const grouped = useMemo(() => {
    const map = new Map<string, DecisionListRow[]>();
    for (const r of rows) {
      const list = map.get(r.category) ?? [];
      list.push(r);
      map.set(r.category, list);
    }
    return Array.from(map.entries());
  }, [rows]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading decision points…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cs-decision-list">
      <p className="text-sm text-muted-foreground">
        {rows.length} registered decision points. Every piece of Creative Strategist AI reasoning
        runs through one of these, and its output is validated against the registered schema before
        any caller sees it.
      </p>

      {grouped.map(([category, list]) => (
        <Card key={category}>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-mono tracking-wide">{category}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {list.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => onOpen(r.key)}
                  className="flex w-full items-center gap-3 px-6 py-3 text-left transition-colors hover:bg-muted/50"
                  data-testid={`row-decision-${r.key}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium">{r.name}</span>
                      <span className="truncate font-mono text-[10px] text-muted-foreground">
                        {r.key}
                      </span>
                    </div>
                    <div className="truncate text-xs text-muted-foreground">{r.description}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <ProviderBadge provider={r.provider} />
                    <Badge variant="outline" className="text-[10px]">
                      {POLICY_LABELS[r.executionPolicy] ?? r.executionPolicy}
                    </Badge>
                    <Badge variant="outline" className="font-mono text-[10px]">
                      v{r.version}
                    </Badge>
                    {!r.seeded ? (
                      <Badge variant="destructive" className="text-[10px]">
                        not seeded
                      </Badge>
                    ) : (
                      <StatusDot ok={r.active} label={r.active ? "Active" : "Inactive"} />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ── Endpoint tester ─────────────────────────────────────────────────────────

function EndpointTester({
  detail,
  endpointUrl,
  authHeaderName,
  authSecretKey,
  inputEnvelope,
  timeoutMs,
  retryPolicy,
  onTested,
}: {
  detail: DecisionDetail;
  endpointUrl: string;
  authHeaderName: string;
  authSecretKey: string;
  inputEnvelope: string;
  timeoutMs: number;
  retryPolicy: { maxRetries: number; baseDelayMs: number; multiplier: number };
  onTested: () => void;
}) {
  const { toast } = useToast();
  const [payloadText, setPayloadText] = useState(() =>
    JSON.stringify(detail.exampleInput, null, 2),
  );
  const [result, setResult] = useState<EndpointTestResult | null>(null);

  const run = useMutation({
    mutationFn: async () => {
      let payload: unknown;
      try {
        payload = JSON.parse(payloadText);
      } catch {
        throw new Error("The example payload is not valid JSON.");
      }
      const res = await apiRequest(
        "POST",
        `/api/admin/creative-strategist/decisions/${encodeURIComponent(detail.key)}/test`,
        // Send the timeout and retry policy on screen too: the test has to run
        // on the same transport the saved configuration will use, or a green
        // receipt would unlock an untested one.
        { endpointUrl, authHeaderName, authSecretKey, inputEnvelope, timeoutMs, retryPolicy, payload },
      );
      return (await res.json()) as EndpointTestResult;
    },
    onSuccess: (r) => {
      setResult(r);
      onTested();
      toast({
        title: r.status === "passed" ? "Test passed" : "Test failed",
        description:
          r.status === "passed"
            ? `Output validated against the schema in ${r.runtimeMs}ms.`
            : r.error ?? "See the validation details below.",
        variant: r.status === "passed" ? undefined : "destructive",
      });
    },
    onError: (e: Error) =>
      toast({ title: "Test failed", description: e.message, variant: "destructive" }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <PlayCircle className="h-4 w-4" />
          Test endpoint
        </CardTitle>
        <CardDescription>
          Posts the payload below to the endpoint above using the auth header, envelope, timeout and
          retry settings currently on screen, then validates the response against this decision's
          output schema. A Sim-backed decision cannot be activated until this passes on the exact
          configuration it will run with — change any of those settings and the test must be re-run.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="cs-test-payload">Example payload</Label>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPayloadText(JSON.stringify(detail.exampleInput, null, 2))}
              data-testid="button-cs-test-reset-payload"
            >
              Load example
            </Button>
          </div>
          <Textarea
            id="cs-test-payload"
            rows={8}
            value={payloadText}
            onChange={(e) => setPayloadText(e.target.value)}
            className="font-mono text-[11px]"
            data-testid="textarea-cs-test-payload"
          />
        </div>

        <Button
          onClick={() => run.mutate()}
          disabled={run.isPending || !endpointUrl.trim()}
          data-testid="button-cs-test-endpoint"
        >
          {run.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Run test
        </Button>

        {result && (
          <div className="space-y-3 rounded-lg border p-4" data-testid="cs-test-result">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <Badge variant={result.status === "passed" ? "default" : "destructive"}>
                {result.status === "passed" ? "PASSED" : "FAILED"}
              </Badge>
              <span className="text-muted-foreground">
                HTTP {result.httpStatus ?? "—"} · {result.runtimeMs}ms
              </span>
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                {result.validation.ran ? (
                  result.validation.valid ? (
                    <>
                      <Check className="h-3.5 w-3.5 text-emerald-500" /> Schema valid
                    </>
                  ) : (
                    <>
                      <X className="h-3.5 w-3.5 text-destructive" /> Schema invalid
                    </>
                  )
                ) : (
                  <>
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-500" /> Validation not reached
                  </>
                )}
              </span>
            </div>

            {result.error && (
              <div className="rounded-md bg-destructive/10 p-3 text-xs text-destructive">
                {result.error}
              </div>
            )}

            {result.validation.missingFields.length > 0 && (
              <div className="text-xs">
                <span className="font-medium">Missing fields: </span>
                <span className="font-mono">{result.validation.missingFields.join(", ")}</span>
              </div>
            )}
            {result.validation.wrongTypes.length > 0 && (
              <div className="text-xs">
                <span className="font-medium">Wrong types: </span>
                <span className="font-mono">{result.validation.wrongTypes.join(", ")}</span>
              </div>
            )}

            <div className="grid gap-3 lg:grid-cols-2">
              <div className="space-y-1">
                <div className="text-xs font-medium">Request body sent</div>
                <JsonBlock value={result.requestBody} />
              </div>
              <div className="space-y-1">
                <div className="text-xs font-medium">Parsed output</div>
                <JsonBlock value={result.parsedOutput ?? "—"} testId="cs-test-parsed" />
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-xs font-medium">Raw output</div>
              <JsonBlock value={result.rawOutput || "—"} testId="cs-test-raw" />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Decision detail ─────────────────────────────────────────────────────────

function RunTable({ runs, emptyLabel }: { runs: DecisionRun[]; emptyLabel: string }) {
  if (runs.length === 0) {
    return <p className="px-6 py-4 text-xs text-muted-foreground">{emptyLabel}</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="border-b text-muted-foreground">
          <tr>
            <th className="px-6 py-2 text-left font-medium">When</th>
            <th className="px-3 py-2 text-left font-medium">Provider</th>
            <th className="px-3 py-2 text-left font-medium">Model / endpoint</th>
            <th className="px-3 py-2 text-left font-medium">Validation</th>
            <th className="px-3 py-2 text-right font-medium">Duration</th>
            <th className="px-3 py-2 text-right font-medium">Retries</th>
            <th className="px-6 py-2 text-left font-medium">Error</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {runs.map((r) => (
            <tr key={r.id} data-testid={`run-${r.id}`}>
              <td className="whitespace-nowrap px-6 py-2">{fmtTime(r.createdAt)}</td>
              <td className="px-3 py-2">
                <ProviderBadge provider={r.provider} />
              </td>
              <td className="max-w-[220px] truncate px-3 py-2 font-mono text-[10px]">
                {r.modelOrEndpoint ?? "—"}
              </td>
              <td className="px-3 py-2">
                <Badge
                  variant={r.validationStatus === "valid" ? "secondary" : "destructive"}
                  className="text-[10px]"
                >
                  {r.validationStatus}
                </Badge>
              </td>
              <td className="px-3 py-2 text-right">{r.durationMs}ms</td>
              <td className="px-3 py-2 text-right">{r.retryCount}</td>
              <td className="max-w-[320px] truncate px-6 py-2 text-destructive">{r.error ?? ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DecisionDetailView({ decisionKey, onBack }: { decisionKey: string; onBack: () => void }) {
  const { toast } = useToast();
  const detailKey = [`/api/admin/creative-strategist/decisions/${decisionKey}`];
  const { data: detail, isLoading } = useQuery<DecisionDetail>({ queryKey: detailKey });

  const [endpointUrl, setEndpointUrl] = useState("");
  const [authHeaderName, setAuthHeaderName] = useState("");
  const [authSecretKey, setAuthSecretKey] = useState("");
  const [provider, setProvider] = useState<"internal" | "sim">("internal");
  const [policy, setPolicy] = useState<string>("internal_only");
  const [envelope, setEnvelope] = useState<string>("wrapped");
  const [timeoutMs, setTimeoutMs] = useState<number>(90000);
  const [maxRetries, setMaxRetries] = useState<number>(2);

  useEffect(() => {
    if (!detail) return;
    setEndpointUrl(detail.endpointUrl ?? "");
    setAuthHeaderName(detail.authHeaderName ?? "X-API-Key");
    setAuthSecretKey(detail.authSecretKey ?? "SIM_STUDIO_API_KEY");
    setProvider(detail.provider);
    setPolicy(detail.executionPolicy);
    setEnvelope(detail.inputEnvelope);
    setTimeoutMs(detail.timeoutMs);
    setMaxRetries(detail.retryPolicy.maxRetries);
  }, [detail]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: detailKey });
    queryClient.invalidateQueries({ queryKey: DECISIONS_KEY });
  };

  const patch = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const res = await apiRequest(
        "PATCH",
        `/api/admin/creative-strategist/decisions/${encodeURIComponent(decisionKey)}`,
        body,
      );
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      toast({ title: "Saved", description: "Decision configuration updated." });
    },
    onError: (e: Error) =>
      toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  if (isLoading || !detail) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading decision…
      </div>
    );
  }

  const usesSim = provider === "sim" || policy !== "internal_only";

  return (
    <div className="space-y-6" data-testid="cs-decision-detail">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="button-cs-decision-back">
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            All decision points
          </Button>
          <h3 className="text-lg font-medium">{detail.name}</h3>
          <div className="font-mono text-xs text-muted-foreground">{detail.key}</div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            {detail.active ? "Active" : "Inactive"}
          </span>
          <Switch
            checked={detail.active}
            disabled={patch.isPending || (!detail.active && !detail.activation.canActivate)}
            onCheckedChange={(v) => patch.mutate({ active: v })}
            data-testid="switch-cs-decision-active"
          />
        </div>
      </div>

      {!detail.activation.canActivate && !detail.active && (
        <div
          className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-xs"
          data-testid="cs-activation-blocked"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
          <span>{detail.activation.reason}</span>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Purpose</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p>{detail.description}</p>
          <div>
            <div className="text-xs font-medium text-muted-foreground">When it runs</div>
            <p className="text-sm">{detail.whenItRuns}</p>
          </div>
          <div>
            <div className="text-xs font-medium text-muted-foreground">Default prompt</div>
            <p className="text-xs leading-relaxed text-muted-foreground">{detail.instruction}</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Execution</CardTitle>
          <CardDescription>
            Registry defaults: {detail.defaults.provider} · {POLICY_LABELS[detail.defaults.executionPolicy] ?? detail.defaults.executionPolicy} ·{" "}
            {detail.defaults.timeoutMs}ms
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Provider</Label>
              <Select value={provider} onValueChange={(v) => setProvider(v as any)}>
                <SelectTrigger data-testid="select-cs-decision-provider">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal">Internal</SelectItem>
                  <SelectItem value="sim">Sim</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Execution policy</Label>
              <Select value={policy} onValueChange={setPolicy}>
                <SelectTrigger data-testid="select-cs-decision-policy">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal_only">Internal Only</SelectItem>
                  <SelectItem value="sim_only">Sim Only</SelectItem>
                  <SelectItem value="sim_with_fallback">Sim With Fallback</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cs-decision-timeout">Timeout (ms)</Label>
              <Input
                id="cs-decision-timeout"
                type="number"
                min={1000}
                max={900000}
                step={1000}
                value={timeoutMs}
                onChange={(e) => setTimeoutMs(Number(e.target.value))}
                data-testid="input-cs-decision-timeout"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cs-decision-retries">Max retries</Label>
              <Input
                id="cs-decision-retries"
                type="number"
                min={0}
                max={10}
                value={maxRetries}
                onChange={(e) => setMaxRetries(Number(e.target.value))}
                data-testid="input-cs-decision-retries"
              />
              <p className="text-xs text-muted-foreground">
                Backoff {detail.retryPolicy.baseDelayMs}ms × {detail.retryPolicy.multiplier}
              </p>
            </div>
          </div>

          {usesSim && (
            <div className="grid gap-4 border-t pt-4 sm:grid-cols-2">
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="cs-decision-endpoint">Sim endpoint URL</Label>
                <Input
                  id="cs-decision-endpoint"
                  value={endpointUrl}
                  onChange={(e) => setEndpointUrl(e.target.value)}
                  placeholder="https://www.sim.ai/api/workflows/…/execute"
                  className="font-mono text-xs"
                  data-testid="input-cs-decision-endpoint"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cs-decision-auth-header">Auth header</Label>
                <Input
                  id="cs-decision-auth-header"
                  value={authHeaderName}
                  onChange={(e) => setAuthHeaderName(e.target.value)}
                  className="font-mono text-xs"
                  data-testid="input-cs-decision-auth-header"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cs-decision-auth-secret">Auth secret name</Label>
                <Input
                  id="cs-decision-auth-secret"
                  value={authSecretKey}
                  onChange={(e) => setAuthSecretKey(e.target.value)}
                  className="font-mono text-xs"
                  data-testid="input-cs-decision-auth-secret"
                />
                <p className="text-xs text-muted-foreground">
                  Name of a SIM_* environment secret. The value is never stored here.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Input envelope</Label>
                <Select value={envelope} onValueChange={setEnvelope}>
                  <SelectTrigger data-testid="select-cs-decision-envelope">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="wrapped">Wrapped — {"{ input: { … } }"}</SelectItem>
                    <SelectItem value="top_level">Top level — {"{ … }"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Last test</Label>
                <div className="flex h-9 items-center gap-2 text-xs">
                  {detail.lastTest?.status === "passed" ? (
                    <Badge variant="default">PASSED</Badge>
                  ) : detail.lastTest?.status ? (
                    <Badge variant="destructive">FAILED</Badge>
                  ) : (
                    <Badge variant="outline">Never tested</Badge>
                  )}
                  <span className="text-muted-foreground">{fmtTime(detail.lastTest?.at)}</span>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end border-t pt-4">
            <Button
              size="sm"
              disabled={patch.isPending}
              onClick={() =>
                patch.mutate({
                  provider,
                  executionPolicy: policy,
                  inputEnvelope: envelope,
                  timeoutMs,
                  endpointUrl: endpointUrl.trim() || null,
                  authHeaderName: authHeaderName.trim() || null,
                  authSecretKey: authSecretKey.trim() || null,
                  retryPolicy: {
                    maxRetries,
                    baseDelayMs: detail.retryPolicy.baseDelayMs,
                    multiplier: detail.retryPolicy.multiplier,
                  },
                })
              }
              data-testid="button-cs-decision-save"
            >
              {patch.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save configuration
            </Button>
          </div>
        </CardContent>
      </Card>

      {usesSim && (
        <EndpointTester
          detail={detail}
          endpointUrl={endpointUrl}
          authHeaderName={authHeaderName}
          authSecretKey={authSecretKey}
          inputEnvelope={envelope}
          timeoutMs={timeoutMs}
          retryPolicy={{
            maxRetries,
            baseDelayMs: detail.retryPolicy.baseDelayMs,
            multiplier: detail.retryPolicy.multiplier,
          }}
          onTested={invalidate}
        />
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Input schema</CardTitle>
          </CardHeader>
          <CardContent>
            <JsonBlock value={detail.inputSchema} testId="cs-input-schema" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Output schema</CardTitle>
          </CardHeader>
          <CardContent>
            <JsonBlock value={detail.outputSchema} testId="cs-output-schema" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Example input</CardTitle>
          </CardHeader>
          <CardContent>
            <JsonBlock value={detail.exampleInput} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Example output</CardTitle>
          </CardHeader>
          <CardContent>
            <JsonBlock value={detail.exampleOutput} />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Recent runs</CardTitle>
          <CardDescription>Last 25 attempts, newest first.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <RunTable runs={detail.recentRuns} emptyLabel="This decision has not run yet." />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Recent errors</CardTitle>
          <CardDescription>
            Attempts that failed or whose output did not validate.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <RunTable runs={detail.recentErrors} emptyLabel="No failures recorded." />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Version history</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {detail.versionHistory.map((v) => (
            <div key={v.version} className="flex gap-3 text-xs">
              <Badge variant="outline" className="h-5 shrink-0 font-mono text-[10px]">
                v{v.version}
              </Badge>
              <span className="shrink-0 text-muted-foreground">{v.date}</span>
              <span>{v.notes}</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Templates (Task #2380, spec §61) ────────────────────────────────────────

interface AdminTemplateRow {
  id: string;
  assetType: string;
  assetTypeLabel: string;
  name: string;
  status: string;
  isDefault: boolean;
  system: string;
  templateType: string | null;
  templateId: string | null;
  source: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  usageCount: number;
}

interface AdminTemplateBrand {
  brandId: string;
  brandName: string;
  readyCount: number;
  assetTypeCount: number;
  approvedCount: number;
  testingCount: number;
  retiredCount: number;
  usageCount: number;
  templates: AdminTemplateRow[];
}

const TEMPLATE_STATUS_TONE: Record<string, string> = {
  DEFAULT: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
  APPROVED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300",
  TESTING: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300",
  UNREVIEWED: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
  RETIRED: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400 line-through",
};

/**
 * Cross-brand coverage. Actions run through the same service the brand page
 * uses, so master approvals cannot widen a brand's underlying access either.
 */
function TemplatesPane() {
  const { toast } = useToast();
  const [openBrand, setOpenBrand] = useState<string | null>(null);

  const { data, isLoading } = useQuery<{ brands: AdminTemplateBrand[] }>({
    queryKey: ["/api/admin/creative-strategist/templates"],
  });

  const act = useMutation({
    mutationFn: async (vars: { templateId: string; action: string }) => {
      await apiRequest(
        "POST",
        `/api/admin/creative-strategist/templates/${vars.templateId}/status`,
        { action: vars.action },
      );
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["/api/admin/creative-strategist/templates"] }),
    onError: (e: Error) =>
      toast({ title: "That didn't work", description: e.message, variant: "destructive" }),
  });

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading template coverage…</div>;
  }

  const brands = data?.brands ?? [];
  if (brands.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          No brand has approved a Creative Strategist template yet.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {brands.map((brand) => {
        const open = openBrand === brand.brandId;
        return (
          <Card key={brand.brandId} data-testid={`card-admin-templates-${brand.brandId}`}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <CardTitle className="text-base">{brand.brandName}</CardTitle>
                  <CardDescription>
                    {brand.readyCount} of {brand.assetTypeCount} asset types have an approved
                    default · {brand.approvedCount} approved · {brand.testingCount} testing ·{" "}
                    {brand.retiredCount} retired · used {brand.usageCount}×
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOpenBrand(open ? null : brand.brandId)}
                  data-testid={`button-toggle-brand-${brand.brandId}`}
                >
                  {open ? "Hide" : `Show ${brand.templates.length}`}
                </Button>
              </div>
            </CardHeader>
            {open && (
              <CardContent className="pt-0">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-xs uppercase tracking-wider text-muted-foreground">
                      <th className="text-left font-medium py-2">Asset type</th>
                      <th className="text-left font-medium py-2">Template</th>
                      <th className="text-left font-medium py-2">Underlying reference</th>
                      <th className="text-right font-medium py-2">Used</th>
                      <th className="text-right font-medium py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {brand.templates.map((t) => (
                      <tr key={t.id} className="border-b last:border-0" data-testid={`row-admin-template-${t.id}`}>
                        <td className="py-2 pr-2">{t.assetTypeLabel}</td>
                        <td className="py-2 pr-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{t.name}</span>
                            <Badge className={TEMPLATE_STATUS_TONE[t.status] ?? ""}>
                              {t.status}
                            </Badge>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {t.source ? `via ${t.source}` : "—"}
                            {t.approvedAt ? ` · approved ${new Date(t.approvedAt).toLocaleDateString()}` : ""}
                          </div>
                        </td>
                        <td className="py-2 pr-2 text-xs font-mono text-muted-foreground break-all">
                          {t.system}
                          {t.templateType ? `/${t.templateType}` : ""}
                          {t.templateId ? `#${t.templateId}` : ""}
                        </td>
                        <td className="py-2 text-right tabular-nums">{t.usageCount}</td>
                        <td className="py-2 text-right whitespace-nowrap">
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={act.isPending || t.status === "APPROVED"}
                            onClick={() => act.mutate({ templateId: t.id, action: "approve" })}
                            data-testid={`button-admin-approve-${t.id}`}
                          >
                            Approve
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={act.isPending || t.isDefault}
                            onClick={() => act.mutate({ templateId: t.id, action: "approve_default" })}
                            data-testid={`button-admin-default-${t.id}`}
                          >
                            Default
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={act.isPending || t.status === "RETIRED"}
                            onClick={() => act.mutate({ templateId: t.id, action: "retire" })}
                            data-testid={`button-admin-retire-${t.id}`}
                          >
                            Retire
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
}

// ── shell ───────────────────────────────────────────────────────────────────

const SUB_TABS = new Set(["general", "runs", "decisions", "templates"]);

export default function CreativeStrategistAdminPane() {
  const [subTab, setSubTab] = useState<string>(() => {
    const s = new URLSearchParams(window.location.search).get("sub");
    return s && SUB_TABS.has(s) ? s : "general";
  });
  const [openKey, setOpenKey] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium">Creative Strategist</h2>
        <p className="text-sm text-muted-foreground">
          Global switch, execution defaults and the AI decision points the feature reasons with.
        </p>
      </div>

      <Tabs value={subTab} onValueChange={setSubTab} className="space-y-4">
        <TabsList data-testid="creative-strategist-subtabs">
          <TabsTrigger value="general" className="gap-1.5" data-testid="subtab-cs-general">
            <Settings2 className="h-4 w-4" />
            General
          </TabsTrigger>
          <TabsTrigger value="decisions" className="gap-1.5" data-testid="subtab-cs-decisions">
            <Compass className="h-4 w-4" />
            AI Decision Points
          </TabsTrigger>
          <TabsTrigger value="templates" className="gap-1.5" data-testid="subtab-cs-templates">
            <Layers className="h-4 w-4" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="runs" className="gap-1.5" data-testid="subtab-cs-runs">
            <Activity className="h-4 w-4" />
            Runs
          </TabsTrigger>
        </TabsList>
        <TabsContent value="general">
          <GeneralPane />
        </TabsContent>
        <TabsContent value="templates">
          <TemplatesPane />
        </TabsContent>
        <TabsContent value="runs">
          <RunsPane />
        </TabsContent>
        <TabsContent value="decisions">
          {openKey ? (
            <DecisionDetailView decisionKey={openKey} onBack={() => setOpenKey(null)} />
          ) : (
            <DecisionList onOpen={setOpenKey} />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
